/**
 * 2026 US Federal Tax Data
 * =========================
 *
 * NOTE: This file contains 2025 IRS-published values extrapolated by
 * ~2.5% inflation adjustment. The IRS typically releases finalized
 * 2026 brackets and standard deductions in November 2026. Always verify
 * against IRS Publication 17 / Rev. Proc. before tax season opens.
 *
 * Verification checklist (re-run before April 2027 tax season):
 *   - Standard deduction amounts
 *   - All 7 brackets per filing status
 *   - CTC amounts / phase-outs (we don't model CTC in v1)
 *   - EITC thresholds (we don't model EITC in v1)
 *
 * Sources (2025 actual, used as base):
 *   - IRS Rev. Proc. 2024-40 (2025 inflation adjustments)
 *   - https://www.irs.gov/newsroom/irs-releases-tax-inflation-adjustments-for-tax-year-2025
 */

export type FilingStatus = 'single' | 'mfj' | 'mfs' | 'hoh';

export interface TaxBracket {
  /** Lower bound of taxable income for this bracket (inclusive). */
  min: number;
  /** Upper bound, exclusive. `null` means the top open-ended bracket. */
  max: number | null;
  /** Marginal rate as a decimal (e.g. 0.22 = 22%). */
  rate: number;
}

export interface FederalYearData {
  taxYear: number;
  /** Standard deduction by filing status (USD). */
  standardDeduction: Record<FilingStatus, number>;
  /** Marginal brackets by filing status, ordered low to high. */
  brackets: Record<FilingStatus, TaxBracket[]>;
  /** Maximum deductible student loan interest (USD). */
  studentLoanInterestMax: number;
}

export const FEDERAL_2026: FederalYearData = {
  taxYear: 2026,
  standardDeduction: {
    single: 15_750,
    mfs: 15_750,
    mfj: 31_500,
    hoh: 23_650,
  },
  brackets: {
    single: [
      { min: 0, max: 12_400, rate: 0.1 },
      { min: 12_400, max: 50_400, rate: 0.12 },
      { min: 50_400, max: 107_500, rate: 0.22 },
      { min: 107_500, max: 205_000, rate: 0.24 },
      { min: 205_000, max: 257_000, rate: 0.32 },
      { min: 257_000, max: 640_000, rate: 0.35 },
      { min: 640_000, max: null, rate: 0.37 },
    ],
    mfj: [
      { min: 0, max: 24_800, rate: 0.1 },
      { min: 24_800, max: 100_800, rate: 0.12 },
      { min: 100_800, max: 215_000, rate: 0.22 },
      { min: 215_000, max: 410_000, rate: 0.24 },
      { min: 410_000, max: 515_000, rate: 0.32 },
      { min: 515_000, max: 768_000, rate: 0.35 },
      { min: 768_000, max: null, rate: 0.37 },
    ],
    mfs: [
      { min: 0, max: 12_400, rate: 0.1 },
      { min: 12_400, max: 50_400, rate: 0.12 },
      { min: 50_400, max: 107_500, rate: 0.22 },
      { min: 107_500, max: 205_000, rate: 0.24 },
      { min: 205_000, max: 257_000, rate: 0.32 },
      { min: 257_000, max: 640_000, rate: 0.35 },
      { min: 640_000, max: null, rate: 0.37 },
    ],
    hoh: [
      { min: 0, max: 17_700, rate: 0.1 },
      { min: 17_700, max: 67_500, rate: 0.12 },
      { min: 67_500, max: 107_500, rate: 0.22 },
      { min: 107_500, max: 205_000, rate: 0.24 },
      { min: 205_000, max: 257_000, rate: 0.32 },
      { min: 257_000, max: 640_000, rate: 0.35 },
      { min: 640_000, max: null, rate: 0.37 },
    ],
  },
  studentLoanInterestMax: 2_500,
};

/** Filing status labels for UI. */
export const FILING_STATUS_LABELS: Record<FilingStatus, string> = {
  single: 'Single',
  mfj: 'Married Filing Jointly',
  mfs: 'Married Filing Separately',
  hoh: 'Head of Household',
};

/** Filing status help text. */
export const FILING_STATUS_HELP: Record<FilingStatus, string> = {
  single:
    'Unmarried or legally separated on Dec 31. Highest standard deduction among single statuses for many people.',
  mfj: 'Married and filing one combined return. Lowest brackets, highest standard deduction.',
  mfs: 'Married but filing separate returns. Sometimes useful with income-driven student loan repayment or large medical expenses.',
  hoh: 'Unmarried (or considered unmarried) with a qualifying person. Lower brackets than Single, higher standard deduction.',
};